<template>
	<div class="b-field" :class="classes">
		<div class="b-field__inputWrapper" @click="$refs.input.focus()">
			<div v-if="hasPrefix" class="b-field__affix b-field__affix--prefix">
				<slot name="prefix">
					{{ prefix }}
				</slot>
			</div>

			<input
				ref="input"
				class="b-field__input"
				v-bind="$attrs"
				v-on="listeners"
				:value="value"
			/>

			<div v-if="hasSuffix" class="b-field__affix b-field__affix--suffix">
				<slot name="suffix">
					{{ suffix }}
				</slot>
			</div>
		</div>

		<div class="b-field__helperText" v-if="_helperText">
			{{ _helperText }}
		</div>
	</div>
</template>

<script>
// function setFilter(input, filter, errMsg) {
//     ["input", "keydown", "keyup", "mousedown", "mouseup", "select", "contextmenu", "drop", "focusout"].forEach(function (event) {
//         input.addEventListener(event, function (e) {
//             if (filter(this.value)) {
//                 // Accepted value
//                 if (["keydown", "mousedown", "focusout"].includes(e.type)) {
//                     this.classList.remove("input-error");
//                     this.setCustomValidity("");
//                 }
//                 this.oldValue = this.value;
//                 this.oldSelectionStart = this.selectionStart;
//                 this.oldSelectionEnd = this.selectionEnd;
//             } else if (this.hasOwnProperty("oldValue")) {
//                 // Rejected value - restore the previous one
//                 this.classList.add("input-error");
//                 this.setCustomValidity(errMsg);
//                 this.reportValidity();
//                 this.value = this.oldValue;
//                 this.setSelectionRange(this.oldSelectionStart, this.oldSelectionEnd);
//             } else {
//                 // Rejected value - nothing to restore
//                 this.value = "";
//             }
//         });
//     });
// }

export default {
	inheritAttrs: false,
	props: {
		value: {
			type: [String, Number],
			default: null,
		},
		suffix: {
			type: String,
			default: "",
		},
		prefix: {
			type: String,
			default: "",
		},
		state: {
			type: String,
			default: "default",
			validator: (value) =>
				["default", "success", "error", "warning"].includes(value),
		},
		helperText: {
			type: [String, Object],
			default: "",
		},
		filter: {
			type: Function,
			default: () => true,
		},
		size: {
			type: String,
			default: "m",
			validator: (value) => ["s", "m", "l", "s/m"].includes(value),
		},
	},
	data() {
		return {
			oldValue: null,
			oldSelectionStart: null,
			oldSelectionEnd: null,
		};
	},
	computed: {
		classes() {
			return [`is-${this.state}`, `b-field--${this.size}`];
		},
		hasPrefix() {
			return (
				this.$slots.prefix ||
				(typeof this.prefix === "string" && this.prefix.length > 0)
			);
		},
		hasSuffix() {
			return (
				this.$slots.suffix ||
				(typeof this.suffix === "string" && this.suffix.length > 0)
			);
		},
		_helperText() {
			if (typeof this.helperText === "string") {
				return this.helperText;
			}

			return this.helperText[this.state];
		},
		listeners() {
			return {
				...this.$listeners,
				...Object.entries(
					[
						"keydown",
						"keyup",
						"mousedown",
						"mouseup",
						"select",
						"contextmenu",
						"drop",
						"focusout",
					].map((evtName) => {
						return [
							evtName,
							(e) => {
								this.commonEvtCallback(e);
								this.$listeners[evtName]?.(e);
							},
						];
					})
				),
				input: (e) => {
					this.commonEvtCallback(e);
				},
			};
		},
	},
	methods: {
		commonEvtCallback(e) {
			const input = this.$refs.input;

			if (this.filter(input.value)) {
				this.oldValue = input.value;
				this.oldSelectionStart = this.selectionStart;
				this.oldSelectionEnd = this.selectionEnd;
				this.$emit("input", input.value);
			} else if (this.oldValue !== null) {
				this.$emit("input", this.oldValue);
				input.setSelectionRange(this.oldSelectionStart, this.oldSelectionEnd);
			} else {
				this.$emit("input", "");
			}

			this.$forceUpdate();
		},
	},
};
</script>

<style lang="scss">
.b-field {
	--background-color: var(--color-neutral-0);
	--color: var(--color-neutral-600);
	--border-color: transparent;

	--color-focus: var(--color-primary-900);
	--border-bottom-color-focus: var(--color-primary-900);

	--background-color-readonly: var(--color-neutral-75);
	--color-readonly: var(--color-neutral-400);

	--color-affix: var(--color-neutral-400);

	width: 100%;
	text-align: left;
}

/* this class is the escaped version of: .b-field--s/m */
.b-field--s\/m {
	--control-height-s-m: 32px;
}

.b-field__inputWrapper {
	display: flex;
	height: var(--control-height-m);
	align-items: center;
	justify-content: space-between;
	border-radius: 8px;
	background: var(--background-color);
	padding: 8px 16px;
	border: 1px solid var(--border-color);
	gap: 1rem;
	cursor: text;

	.b-field--s\/m & {
		height: var(--control-height-s-m);
		padding: 7px 10px;
	}
}

.b-field__input {
	color: var(--color);
	background: transparent;
	width: 100%;
	flex: 1 0 0;
	text-align: inherit;
}

.b-field__affix {
	font-size: var(--font-size-xs);
	color: var(--color-affix);
}

.b-field__helperText {
	font-size: var(--font-size-s);
	color: var(--color-neutral-600);
	padding-left: 16px;
	margin-top: 4px;
}

.b-field__inputWrapper:has(:focus) {
	border-bottom-left-radius: 0;
	border-bottom-right-radius: 0;
	border-bottom-color: var(--border-bottom-color-focus);
}

.b-field__inputWrapper:hover .b-field__input,
.b-field__input:focus {
	color: var(--color-focus);
}

.b-field.is-warning {
	.b-field__inputWrapper {
		border-bottom-color: var(--color-warning-300);
	}
}

.b-field.is-error {
	.b-field__inputWrapper {
		border-bottom-color: var(--color-error-300);
	}
}

.b-field__inputWrapper:has([readonly]) {
	background-color: var(--background-color-readonly);

	.b-field__input {
		color: var(--color-readonly);
	}
}

.colorCtx-dark {
	.b-field {
		--background-color: transparent;
		--color: var(--color-primary-50);
		--border-color: var(--color-primary-400);

		--color-focus: var(--color-primary-100);
		--border-bottom-color-focus: var(--color-primary-100);

		// todo
		--background-color-readonly: var(--color-neutral-75);
		--color-readonly: var(--color-neutral-400);

		--color-affix: var(--color-neutral-0);
	}
}
</style>
